import { useMemo, useState } from "react";
import { Loader2, Trash2, Search, Film, ListVideo } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { DAYS, QUARTERS, fmtMin, fmtDur } from "./linearShared";

const sel = "w-full h-9 bg-black/30 border border-gray-800 rounded-md px-2 text-sm text-white";

const Picker = ({ library, kind, value, onPick }) => {
  const [q, setQ] = useState("");
  const rows = useMemo(() => {
    const src = kind === "video" ? library.videos : library.playlists;
    const n = q.trim().toLowerCase();
    return src.filter((r) => !n || `${r.title || r.name} ${r.church}`.toLowerCase().includes(n)).slice(0, 200);
  }, [library, kind, q]);
  return (
    <div className="rounded-xl border border-gray-800 overflow-hidden">
      <div className="relative border-b border-gray-800"><Search className="h-4 w-4 text-gray-500 absolute left-3 top-1/2 -translate-y-1/2" /><input data-testid="slot-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder={`Search ${kind === "video" ? "videos" : "playlists"} or churches…`} className="w-full bg-transparent pl-9 pr-3 py-2.5 text-sm text-white outline-none" /></div>
      <div className="max-h-56 overflow-y-auto divide-y divide-gray-800/60">
        {rows.length === 0 && <p className="px-4 py-6 text-xs text-gray-500 text-center">Nothing found.</p>}
        {rows.map((r) => {
          const on = value && value.id === r.id;
          return (
            <button key={`${r.kind || "pl"}-${r.id}`} data-testid={`slot-pick-${r.id}`} onClick={() => onPick(r)} className={"w-full text-left px-4 py-2 flex items-center gap-3 transition-colors " + (on ? "bg-amber-500/15" : "hover:bg-white/[0.03]")}>
              <div className="flex-1 min-w-0"><p className="text-sm text-white truncate">{r.title || r.name}</p><p className="text-[11px] text-gray-500 truncate">{r.church || "—"}</p></div>
              <span className="text-[11px] text-gray-400 shrink-0">{kind === "video" ? fmtDur(r.duration_seconds) : `${r.count} videos`}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
};

export const SlotDialog = ({ slot, library, onClose, onSaved }) => {
  const isNew = !slot.id;
  const [day, setDay] = useState(slot.day);
  const [start, setStart] = useState(slot.start_min);
  const [end, setEnd] = useState(slot.end_min);
  const [kind, setKind] = useState(slot.content_kind || "video");
  const [pick, setPick] = useState(slot.content_id ? { id: slot.content_id, kind: slot.video_kind, title: slot.title } : null);
  const [busy, setBusy] = useState("");

  const save = async () => {
    setBusy("save");
    const body = { day, start_min: start, end_min: end, content_kind: kind, content_id: pick.id, video_kind: kind === "video" ? pick.kind : null };
    try { isNew ? await api.post("/linear/slots", body) : await api.put(`/linear/slots/${slot.id}`, body); toast.success(isNew ? "Slot added" : "Slot updated"); onSaved(); onClose(); }
    catch (e) { toast.error(e.response?.data?.detail || "Couldn't save the slot"); }
    finally { setBusy(""); }
  };
  const remove = async () => {
    setBusy("del");
    try { await api.delete(`/linear/slots/${slot.id}`); toast.success("Slot removed"); onSaved(); onClose(); }
    catch { toast.error("Couldn't remove"); setBusy(""); }
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent data-testid="slot-dialog" className="bg-[#0E131F] border-gray-800 text-white sm:max-w-[560px]">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">{isNew ? "Schedule a slot" : "Edit slot"}</DialogTitle>
          <DialogDescription className="text-gray-400 text-sm">Times are Eastern. Content that ends early is followed by the fallback playlist; anything still playing is cut when the slot ends.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-3 gap-3">
          <div className="space-y-1"><p className="text-xs text-gray-400">Day</p><select data-testid="slot-day" value={day} onChange={(e) => setDay(Number(e.target.value))} className={sel}>{DAYS.map((d, i) => <option key={d} value={i}>{d}</option>)}</select></div>
          <div className="space-y-1"><p className="text-xs text-gray-400">Starts</p><select data-testid="slot-start" value={start} onChange={(e) => { const s = Number(e.target.value); setStart(s); if (end <= s) setEnd(Math.min(1440, s + 15)); }} className={sel}>{QUARTERS.slice(0, 96).map((m) => <option key={m} value={m}>{fmtMin(m)}</option>)}</select></div>
          <div className="space-y-1"><p className="text-xs text-gray-400">Ends</p><select data-testid="slot-end" value={end} onChange={(e) => setEnd(Number(e.target.value))} className={sel}>{QUARTERS.filter((m) => m > start).map((m) => <option key={m} value={m}>{fmtMin(m).replace(" (end of day)", " (midnight)")}</option>)}</select></div>
        </div>
        <p className="text-xs text-gray-500 -mt-1">Length: {fmtDur((end - start) * 60)}</p>
        <div className="flex gap-2">
          {[["video", "Video", Film], ["playlist", "Playlist", ListVideo]].map(([k, l, I]) => (
            <button key={k} data-testid={`slot-kind-${k}`} onClick={() => { setKind(k); setPick(null); }} className={"flex-1 inline-flex items-center justify-center gap-2 py-2 rounded-lg border text-sm font-medium transition-colors " + (kind === k ? "border-amber-500/60 bg-amber-500/10 text-amber-200" : "border-gray-800 text-gray-400 hover:border-gray-700")}><I className="h-4 w-4" /> {l}</button>
          ))}
        </div>
        <Picker library={library} kind={kind} value={pick} onPick={setPick} />
        {pick && <p data-testid="slot-picked" className="text-xs text-gray-400">Selected: <span className="text-white font-medium">{pick.title || pick.name}</span></p>}
        <div className="mt-2 flex items-center gap-3">
          {!isNew && <button data-testid="slot-delete" onClick={remove} disabled={!!busy} className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-red-400"><Trash2 className="h-4 w-4" /> Remove</button>}
          <button onClick={onClose} className="ml-auto rounded-lg px-4 py-2 text-sm text-gray-400 hover:text-white">Cancel</button>
          <button data-testid="slot-save" onClick={save} disabled={!!busy || !pick} className="inline-flex items-center gap-2 rounded-lg gold-gradient px-4 py-2 text-sm font-semibold text-black hover:brightness-110 disabled:opacity-50">{busy === "save" && <Loader2 className="h-4 w-4 animate-spin" />} {isNew ? "Add slot" : "Save"}</button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
